/**
 * Auth state global (Zustand).
 *
 * Source-of-truth single: store. Persistência em `auth-storage.ts` (não
 * usamos `persist` middleware do Zustand porque queremos controle fino
 * de local vs session storage baseado em "remember").
 *
 * O store é inicializado lendo do storage no boot (ver `init`).
 *
 * ## Refresh token (TD26-31)
 *
 * Backend (`apps/backend/app/api/endpoints/auth.py`) emite par (access,
 * refresh) com expirações configuráveis:
 * - `ACCESS_TOKEN_EXPIRE_MINUTES` (typ. 60min em prod legacy)
 * - `REFRESH_TOKEN_EXPIRE_MINUTES` (typ. ~7 dias)
 *
 * Frontend roda dois flows complementares:
 *
 * 1. **Proativo**: ao logar/iniciar, decodifica `exp` do access JWT e agenda
 *    `setTimeout` pra chamar `/token/refresh` 60s ANTES do expiry. Janela
 *    de 60s absorve clock drift entre cliente e servidor + latência da
 *    request. Cada refresh re-agenda o próximo. Cancelado em `logout()`.
 *
 * 2. **Reativo**: em `api.ts`, qualquer 401 num request (não-`skipAuth`)
 *    dispara `refreshAccessToken()` single-flight e re-tenta o request
 *    com o novo bearer. Se o refresh também falhar (refresh expirado /
 *    inválido), cai em `logout()`.
 *
 * Single-flight: 5 queries paralelas tomando 401 simultâneo compartilham
 * UMA chamada de `/token/refresh` via promise memoizada. Sem isso, refresh
 * token seria invalidado N-1 vezes (rotação do backend).
 */

import { refreshTokens } from '@/features/auth/api';
import { create } from 'zustand';
import { ApiError } from './api';
import { type AuthBundle, clearAuth, loadAuth, saveAuth, updateTokens } from './auth-storage';

export type AuthStatus = 'initializing' | 'authenticated' | 'anonymous';

type AuthState = {
  status: AuthStatus;
  accessToken: string | null;
  refreshToken: string | null;
  username: string | null;
};

type AuthActions = {
  /** Lê storage e seta status. Chamar 1x no boot, antes do RouterProvider. */
  init: () => void;
  /** Login bem-sucedido — persiste e marca autenticado. */
  login: (bundle: AuthBundle, remember: boolean) => void;
  /** Logout — limpa storage, cancela refresh agendado, marca anônimo. */
  logout: () => void;
  /**
   * Troca o access (+ refresh, backend rotaciona) usando o refresh atual.
   * Single-flight: chamadas concorrentes compartilham o mesmo Promise.
   * Retorna true se bem-sucedido, false se falhou (e já chamou logout).
   */
  refreshAccessToken: () => Promise<boolean>;
};

// ─── Estado fora da store (não-tracked, não-serializável) ──────────────────
//
// `refreshPromise` faz o single-flight: enquanto uma chamada de /token/refresh
// está pendente, qualquer outra `refreshAccessToken()` aguarda esse mesmo
// Promise. `refreshTimerId` segura o setTimeout proativo pra cancelar em
// logout / re-agendar em sucessivos refresh.

let refreshPromise: Promise<boolean> | null = null;
let refreshTimerId: number | null = null;

/**
 * Decodifica o claim `exp` (epoch em segundos) de um JWT em base64url.
 * Não valida assinatura — só lê o middle segment. Retorna ms epoch ou null.
 *
 * Tolerante a token mal formado / não-JWT (retorna null em vez de throw).
 */
function decodeJwtExpMs(token: string): number | null {
  try {
    const parts = token.split('.');
    const middleRaw = parts[1];
    if (parts.length !== 3 || !middleRaw) return null;
    // base64url → base64 padding
    const middle = middleRaw.replace(/-/g, '+').replace(/_/g, '/');
    const padded = middle + '='.repeat((4 - (middle.length % 4)) % 4);
    const json = atob(padded);
    const payload = JSON.parse(json);
    if (typeof payload.exp !== 'number') return null;
    return payload.exp * 1000;
  } catch {
    return null;
  }
}

/** 60s antes do expiry. Janela absorve clock drift + latência da request. */
const REFRESH_LEEWAY_MS = 60_000;
/** Se exp já passou ou está dentro da leeway, refresh imediato. */
const MIN_DELAY_MS = 0;

function cancelScheduledRefresh(): void {
  if (refreshTimerId != null) {
    window.clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }
}

/**
 * Agenda refresh proativo pra `exp - 60s`. Chamada após login e em init
 * (se restaurou sessão). Idempotente — cancela timer anterior.
 *
 * Se o access já está dentro da janela de 60s (ou já expirou), dispara
 * refresh imediato. Se exp não é decodificável, no-op silencioso — o
 * refresh reativo (via api.ts 401) cobre.
 */
function scheduleRefresh(accessToken: string): void {
  cancelScheduledRefresh();
  const expMs = decodeJwtExpMs(accessToken);
  if (expMs == null) return;
  const delay = Math.max(MIN_DELAY_MS, expMs - Date.now() - REFRESH_LEEWAY_MS);
  refreshTimerId = window.setTimeout(() => {
    refreshTimerId = null;
    // void: erros já tratam dentro (logout em falha)
    void useAuthStore.getState().refreshAccessToken();
  }, delay);
}

export const useAuthStore = create<AuthState & AuthActions>((set, get) => ({
  status: 'initializing',
  accessToken: null,
  refreshToken: null,
  username: null,

  init: () => {
    const bundle = loadAuth();
    if (bundle) {
      set({
        status: 'authenticated',
        accessToken: bundle.accessToken,
        refreshToken: bundle.refreshToken,
        username: bundle.username,
      });
      scheduleRefresh(bundle.accessToken);
    } else {
      set({ status: 'anonymous', accessToken: null, refreshToken: null, username: null });
    }
  },

  login: (bundle, remember) => {
    saveAuth(bundle, remember);
    set({
      status: 'authenticated',
      accessToken: bundle.accessToken,
      refreshToken: bundle.refreshToken,
      username: bundle.username,
    });
    scheduleRefresh(bundle.accessToken);
  },

  logout: () => {
    cancelScheduledRefresh();
    refreshPromise = null;
    clearAuth();
    set({ status: 'anonymous', accessToken: null, refreshToken: null, username: null });
  },

  refreshAccessToken: () => {
    if (refreshPromise) return refreshPromise;

    const current = get().refreshToken;
    if (!current) {
      // Sem refresh token (anônimo ou storage limpo) — vai pra logout.
      get().logout();
      return Promise.resolve(false);
    }

    refreshPromise = (async (): Promise<boolean> => {
      try {
        const res = await refreshTokens(current);
        const newBundle: AuthBundle = {
          accessToken: res.access_token,
          refreshToken: res.refresh_token,
          username: get().username ?? '',
        };
        updateTokens(newBundle);
        set({ accessToken: newBundle.accessToken, refreshToken: newBundle.refreshToken });
        scheduleRefresh(newBundle.accessToken);
        return true;
      } catch (err) {
        // 401 (refresh expirado/inválido) ou network → logout.
        // ApiError com status 0 (rede) NÃO desloga: re-agenda pra próximo
        // tick e deixa o reativo cobrir quando a rede voltar. Mas se for
        // 401 explícito do backend, refresh tá morto — logout.
        if (err instanceof ApiError && err.status === 0) {
          // Rede caiu. Re-agenda em 30s pra tentar de novo.
          cancelScheduledRefresh();
          refreshTimerId = window.setTimeout(() => {
            refreshTimerId = null;
            void useAuthStore.getState().refreshAccessToken();
          }, 30_000);
          return false;
        }
        get().logout();
        return false;
      } finally {
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  },
}));

/** Helper p/ leitores que não querem instanciar selector. */
export function getAccessToken(): string | null {
  return useAuthStore.getState().accessToken;
}
